const SETTINGS_ORIGIN = 'https://settings.imai.local';

export const SETTINGS_RETURN_TO_PARAM = 'returnTo';

function isSettingsPath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  return segments[0] === 'settings' || segments[1] === 'settings';
}

export function sanitizeSettingsReturnPath(
  returnPath: string | null | undefined
): string | null {
  if (
    !returnPath?.startsWith('/') ||
    returnPath.startsWith('//') ||
    returnPath.includes('\\')
  ) {
    return null;
  }

  const parsed = new URL(returnPath, SETTINGS_ORIGIN);
  if (
    parsed.origin !== SETTINGS_ORIGIN ||
    parsed.pathname.startsWith('//') ||
    isSettingsPath(parsed.pathname)
  ) {
    return null;
  }

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

export function buildSettingsHref(
  href: string,
  returnPath: string | null | undefined,
  workspaceSlug?: string | null
): string {
  const safeReturnPath = sanitizeSettingsReturnPath(returnPath);
  if (!safeReturnPath && !workspaceSlug) return href;

  const parsed = new URL(href, SETTINGS_ORIGIN);
  if (safeReturnPath)
    parsed.searchParams.set(SETTINGS_RETURN_TO_PARAM, safeReturnPath);
  if (workspaceSlug && parsed.pathname.startsWith('/settings/'))
    parsed.searchParams.set('workspace', workspaceSlug);
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

export function getSettingsReturnPath(args: {
  pathname: string;
  search: string;
}): string | null {
  const params = new URLSearchParams(args.search);
  const existingReturnPath = sanitizeSettingsReturnPath(
    params.get(SETTINGS_RETURN_TO_PARAM)
  );
  if (existingReturnPath) return existingReturnPath;
  if (isSettingsPath(args.pathname)) return null;

  params.delete(SETTINGS_RETURN_TO_PARAM);
  const search = params.toString();
  return sanitizeSettingsReturnPath(
    `${args.pathname}${search ? `?${search}` : ''}`
  );
}

export function getSettingsBackPath(args: {
  returnPath: string | null | undefined;
  fallbackOrgSlug: string | null | undefined;
}): string | null {
  const safeReturnPath = sanitizeSettingsReturnPath(args.returnPath);
  if (safeReturnPath) return safeReturnPath;
  return args.fallbackOrgSlug ? `/${args.fallbackOrgSlug}/design` : null;
}

export const SETTINGS_FROM_PARAM = 'from';

// Settings pages the pricing page may return to. `returnTo` cannot carry
// these because it only names pages outside settings.
const PRICING_RETURN_SETTING_SLUGS = new Set([
  'archived-chats',
  'analytics',
  'billing',
  'file-permissions',
  'members',
  'memories',
  'profile',
  'permissions',
  'roles',
  'storage',
  'teams',
  'usage',
  'workspace',
]);

/** Links a settings page to the pricing page so its back button returns there. */
/** Sections of the pricing page a link can open at (element ids). */
export type PricingSection = 'plans' | 'credit-packs';

export function buildSettingsPricingHref(args: {
  orgSlug: string;
  fromSettingSlug: string;
  returnPath: string | null | undefined;
  section?: PricingSection;
}): string {
  const parsed = new URL(
    buildSettingsHref(`/${args.orgSlug}/settings/purchase`, args.returnPath),
    SETTINGS_ORIGIN
  );
  if (PRICING_RETURN_SETTING_SLUGS.has(args.fromSettingSlug)) {
    parsed.searchParams.set(SETTINGS_FROM_PARAM, args.fromSettingSlug);
  }
  if (args.section) {
    parsed.hash = args.section;
  }
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

export type PricingBackTarget = { kind: 'settings' | 'app'; href: string };

/** Where the pricing page's back button goes: the linking settings page, else the app. */
export function getPricingBackTarget(args: {
  orgSlug: string;
  from: string | null | undefined;
  returnPath: string | null | undefined;
}): PricingBackTarget | null {
  if (args.from && PRICING_RETURN_SETTING_SLUGS.has(args.from)) {
    return {
      kind: 'settings',
      href: buildSettingsHref(
        `/${args.orgSlug}/settings/${args.from}`,
        args.returnPath
      ),
    };
  }
  const href = getSettingsBackPath({
    returnPath: args.returnPath,
    fallbackOrgSlug: args.orgSlug,
  });
  return href ? { kind: 'app', href } : null;
}

export function getWorkspaceSlugFromSettingsReturnPath(
  returnPath: string | null | undefined
): string | null {
  const safeReturnPath = sanitizeSettingsReturnPath(returnPath);
  return safeReturnPath?.split('/').filter(Boolean)[0] ?? null;
}

export function retargetSettingsReturnPath(args: {
  returnPath: string | null | undefined;
  currentOrgSlug: string;
  targetOrgSlug: string;
}): string | null {
  const safeReturnPath = sanitizeSettingsReturnPath(args.returnPath);
  if (!safeReturnPath) return null;
  const parsed = new URL(safeReturnPath, SETTINGS_ORIGIN);
  const segments = parsed.pathname.split('/').filter(Boolean);
  if (segments[0] !== args.currentOrgSlug) return safeReturnPath;
  segments[0] = args.targetOrgSlug;
  return `/${segments.join('/')}${parsed.search}${parsed.hash}`;
}
